
import React, { useState } from 'react';
import { useDispatch } from 'react-redux';
import { Eye, EyeOff, CheckCircle, XCircle, Info } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Progress } from '@/components/ui/progress';
import { useToast } from '@/hooks/use-toast';
import { loginSuccess } from '@/store/slices/authSlice';
import { signUpVendor, login ,sendVendorSignupOtpToEmail} from '@/store/thunkMidddleware/authMiddleware';
import { getVendorProfile } from '@/store/thunkMidddleware/usersMiddleware';
import { useNavigate } from "react-router-dom";
import { ButtonWithLoader } from '../ui/buttonwithloader';
import { useAppDispatch, useAppSelector } from '@/hooks/useReduxHooks'
import routes from '@/routes/routes.json'
import { Dialog, DialogContent, DialogHeader, DialogFooter, DialogTitle } from "@/components/ui/dialog"
import { SignUpEmailVerificationPayload} from "@/types/interfaceType";
import { InputOTP, InputOTPGroup, InputOTPSlot } from "@/components/ui/input-otp";
import Antigravity from '@/assets/animation/Antigravity';

interface SignupScreenProps {
  onSwitchToLogin: () => void;
}

type EmailOtpDialogProps = {
  showEmailOtpModal: boolean;
  setShowEmailOtpModal: (val: boolean) => void;
  email:string,
  vendorDetails:SignUpEmailVerificationPayload
  onConfirm?: () => void; // callback when vendor confirms
  handleEmailLogin:(email:string,password:string)=>void
};


export const EmailOtpDialog = ({
  showEmailOtpModal,
  setShowEmailOtpModal,
  email,
  vendorDetails,
  handleEmailLogin
}: EmailOtpDialogProps) => {
  const { toast } = useToast();
  const dispatch = useAppDispatch();

  const [otp,setOtp] = useState('')
  const authState = useAppSelector((state) => state.auth);
  const profileState = useAppSelector((state) => state.profile);

  const submit=()=>{
    let payload ={
      ...vendorDetails,
      otp
    }

       dispatch(signUpVendor({
        payload: payload,
        callback: (res) => {
          if(res?.payload){
            setShowEmailOtpModal(false)
            handleEmailLogin(payload?.email, payload?.password)
          }
        }
      }))
    
  }
  return (
    <Dialog
      open={showEmailOtpModal}
      onOpenChange={(isOpen) => {
        setOtp('')
        setShowEmailOtpModal(isOpen)
      }}
    >
      <DialogContent className="max-w-[500px] max-h-[90vh] p-0 bg-flup-surface flex flex-col">
        {/* Header */}
        <DialogHeader className="p-4 border-b">
          <DialogTitle className="text-lg font-semibold text-black/80">
            Enter Your OTP
          </DialogTitle>
        </DialogHeader>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-6  space-y-4 pb-4">
          <p className="text-sm text-black/80 flex items-center space-x-2">
           <Info className='w-4 h-4 text-orange-500'/> <span> 
           OTP has trigged to: <span className='font-bold'>{email} </span> 
          </span>
          </p>

           <div className="space-y-2 flex justify-start">
                <div className="space-y-2">
                  <Label className="text-sm font-medium text-flup-ink-2">
                    Enter 6-digit code
                  </Label>
                  <div className="flex justify-start">
                    <InputOTP
                      maxLength={6}
                      value={otp}
                      onChange={(value) => setOtp(value)}
                    >
                      <InputOTPGroup >
                        <InputOTPSlot index={0} className='p-4' />
                        <InputOTPSlot index={1} className='p-4' />
                        <InputOTPSlot index={2} className='p-4' />
                        <InputOTPSlot index={3} className='p-4' />
                        <InputOTPSlot index={4} className='p-4' />
                        <InputOTPSlot index={5} className='p-4' />
                      </InputOTPGroup>
                    </InputOTP>
                  </div>
                  {/* {timeLeft > 0 && <Label className="text-xs  text-flup-ink-3">
                    Resend opt in: <span className='text-blue-800 font-bold'> {`${timeLeft} sec.`}</span>
                  </Label>} */}
                </div>
              </div>

        </div>

        {/* Footer */}
        <DialogFooter className="p-4 border-t flex gap-4">
          <Button
            variant="outline"
            onClick={() => {
              setOtp('')
              setShowEmailOtpModal(false)
            }}
            className="flex-1"
            disabled={authState.signUpVendorResponse.isLoading || profileState.vendorProfile.isLoading || authState.isLoading}
          >
            Cancel
          </Button>
          <ButtonWithLoader
          isLoading={authState.signUpVendorResponse.isLoading || profileState.vendorProfile.isLoading || authState.isLoading}

          disabled={otp?.length != 6 ||authState.signUpVendorResponse.isLoading || profileState.vendorProfile.isLoading || authState.isLoading}
          onClick={() => {
              // onConfirm();
              submit()
            }}
            className="flex-1 bg-green-500 text-white hover:bg-green-600"
          >
            Proceed
          </ButtonWithLoader>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};




const SignupScreen: React.FC<SignupScreenProps> = ({ onSwitchToLogin }) => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate()
  const authState = useAppSelector(state => state.auth)
  const profileState = useAppSelector(state => state.profile)

  const { toast } = useToast();

  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    password: '',
    confirmPassword: '',
  });
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showEmailOtpModal,setShowEmailOtpModal]=useState<boolean>(false)
  // const getPasswordStrength = (password: string): number => {
  //   let strength = 0;
  //   if (password.length >= 8) strength += 25;
  //   if (/[A-Z]/.test(password)) strength += 25;
  //   if (/[a-z]/.test(password)) strength += 25;
  //   if (/[0-9]/.test(password)) strength += 25;
  //   return strength;
  // };

  const getPasswordStrength = (password: string): number => {
    let strength = 0;
    if (password.length >= 8) strength += 20;
    if (/[A-Z]/.test(password)) strength += 20;
    if (/[a-z]/.test(password)) strength += 20;
    if (/[0-9]/.test(password)) strength += 20;
    if (/[^A-Za-z0-9]/.test(password)) strength += 20; // Special character
    return strength;
  };

  const passwordStrength = getPasswordStrength(formData.password);

  const getPasswordStrengthLabel = (strength: number): string => {
    if (strength < 25) return 'Weak';
    if (strength < 50) return 'Fair';
    if (strength < 75) return 'Good';
    return 'Strong';
  };

  const getPasswordStrengthColor = (strength: number): string => {
    if (strength < 25) return 'bg-red-500';
    if (strength < 50) return 'bg-orange-500';
    if (strength < 75) return 'bg-yellow-500';
    return 'bg-green-500';
  };

  const validateEmail = (email: string): boolean => {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  };

  const validatePhone = (phone: string): boolean => {
    return /^[+]?[1-9][\d]{9,14}$/.test(phone);
  };

  const handleInputChange = (field: string, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const fetchProfile = async (token: string) => {
    dispatch(getVendorProfile({
      token: token,
      callback: (profile) => {
        if (profile?.payload?.vendorProfileCompleted) {
          // Straight to the slot board — managing today's availability is what
          // a vendor signs in to do, so it is the landing screen, not a stop on
          // the way to one.
          navigate(routes.venueManagement)
        } else {
          navigate(routes.vendorOnboard)

        }
        console.log("getVendorProfile:", profile)
      }
    }))
  }

  const handleEmailLogin = async (email: string, password: string) => {
    if (!email || !password) {
      toast({ title: 'Error', description: 'Please fill all fields', variant: 'destructive' });
      return;
    }
    // dispatch(loginStart());
    let payload = {
      email: email,
      password: password
    }
    dispatch(login({
      credentials: payload, callback: (res) => {
        console.log("res:", res)
        if (res) {
          fetchProfile(res?.payload?.token)
        }
      }
    }))

  };



  const handleSubmit = async () => {
    sessionStorage.clear()
    localStorage.clear()
    if (!formData.firstName || !formData.lastName || !formData.email || !formData.phone || !formData.password) {
      toast({ title: 'Error', description: 'Please fill all required fields', variant: 'destructive' });
      return;
    }

    if (!validateEmail(formData.email)) {
      toast({ title: 'Error', description: 'Please enter a valid email address', variant: 'destructive' });
      return;
    }

    if (!validatePhone(formData.phone)) {
      toast({ title: 'Error', description: 'Please enter a valid phone number', variant: 'destructive' });
      return;
    }

    if (formData.password !== formData.confirmPassword) {
      toast({ title: 'Error', description: 'Passwords do not match', variant: 'destructive' });
      return;
    }

    if (passwordStrength < 50) {
      toast({ title: 'Error', description: 'Password is too weak', variant: 'destructive' });
      return;
    }

    // setLoading(true);
    try {
      // Simulate API call
      let payloadData = {
        firstName: formData.firstName,
        lastName: formData.lastName,
        email: formData.email,
        phone: formData.phone,
        password: formData.confirmPassword,
        // otp:otp
      }

      dispatch(sendVendorSignupOtpToEmail({payload:payloadData,callback:(res)=>{
        if(res?.payload){
          console.log("sendVendorSignupOtpToEmail:",res.payload)
          setShowEmailOtpModal(true)
        }
      }}))

      // dispatch(signUpVendor({
      //   payload: payloadData,
      //   callback: (res) => {

      //     handleEmailLogin(payloadData?.email, payloadData?.password)
         
      //   }
      // }))

 
    } catch (err) {
      toast({ title: 'Error', description: 'Failed to create account', variant: 'destructive' });
    } finally {
      // setLoading(false);
    }
  };

  const resetStateDataAfterOptModalClosed=()=>{
    setFormData
  }

  return (
    // <div className="min-h-screen flex items-center justify-center bg-flup-brand hover:bg-flup-brand-press text-white p-4">
   <>
   <EmailOtpDialog
   email={formData.email}
   vendorDetails={formData}
   setShowEmailOtpModal={setShowEmailOtpModal}
   showEmailOtpModal={showEmailOtpModal}
   handleEmailLogin={handleEmailLogin}
   />

        <div style={{ width: '100%', height: '100%', position: 'absolute',zIndex:1 }}>
          <Antigravity
            count={500}
            magnetRadius={6}
            ringRadius={7}
            waveSpeed={0.4}
            waveAmplitude={1}
            particleSize={1.6}
            lerpSpeed={0.05}
            // color={'#7400c2'}
            color={'#b6ece1'}

            autoAnimate={true}
            particleVariance={1}
            particleShape={"sphere"}
            // particleShape={"capsule"}
            // particleShape={"tetrahedron"} 
          />
        </div>
  
   <div className="min-h-screen flex items-center justify-center bg-[url('/bg4.jpeg')] bg-cover bg-center bg-center bg-no-repeat bg-fixed">

  {/* Gray overlay */}
      <div className="absolute inset-0 bg-flup-ink/55"></div>
      <Card className="w-full max-w-md m-2 sm:m-0" style={{zIndex:10}}>
        <CardHeader className="text-center">
          <CardTitle className="text-2xl font-bold">Create Account</CardTitle>
          <CardDescription>Join our vendor platform</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="firstName">First Name *</Label>
              <Input
                id="firstName"
                value={formData.firstName}
                onChange={(e) => handleInputChange('firstName', e.target.value)}
                placeholder="John"
                disabled={authState.signUpVendorResponse.isLoading || profileState.vendorProfile.isLoading || authState.isLoading}

              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="lastName">Last Name *</Label>
              <Input
                id="lastName"
                value={formData.lastName}
                onChange={(e) => handleInputChange('lastName', e.target.value)}
                placeholder="Doe"
                disabled={authState.signUpVendorResponse.isLoading || profileState.vendorProfile.isLoading || authState.isLoading}

              />
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="email">Email *</Label>
            <div className="relative">
              <Input
                id="email"
                type="email"
                value={formData.email}
                onChange={(e) => handleInputChange('email', e.target.value)}
                placeholder="john@example.com"
                disabled={authState.signUpVendorResponse.isLoading || profileState.vendorProfile.isLoading || authState.isLoading}

              />
              {formData.email && (
                <div className="absolute right-3 top-3">
                  {validateEmail(formData.email) ? (
                    <CheckCircle className="h-4 w-4 text-green-500" />
                  ) : (
                    <XCircle className="h-4 w-4 text-red-500" />
                  )}
                </div>
              )}
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="phone">Phone Number *</Label>
            <div className="relative">
              <Input
                id="phone"
                type="tel"
                value={formData.phone}
                onChange={(e) => handleInputChange('phone', e.target.value)}
                maxLength={10}
                placeholder="+91 9876543210"
                disabled={authState.signUpVendorResponse.isLoading || profileState.vendorProfile.isLoading || authState.isLoading}

              />
              {formData.phone && (
                <div className="absolute right-3 top-3">
                  {validatePhone(formData.phone) ? (
                    <CheckCircle className="h-4 w-4 text-green-500" />
                  ) : (
                    <XCircle className="h-4 w-4 text-red-500" />
                  )}
                </div>
              )}
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="password">Password *</Label>
            <div className="relative">
              <Input
                id="password"
                type={showPassword ? 'text' : 'password'}
                value={formData.password}
                onChange={(e) => handleInputChange('password', e.target.value)}
                placeholder="Enter your password"
                disabled={authState.signUpVendorResponse.isLoading || profileState.vendorProfile.isLoading || authState.isLoading}

              />
              <Button
                type="button"
                variant="none"
                size="sm"
                className="absolute right-0 top-0 h-full px-3 py-2 hover:bg-transparent hover:text-blue-800"
                onClick={() => setShowPassword(!showPassword)}
              >
                {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </Button>

            </div>
            {formData.password && passwordStrength < 100 && <p className='text-xs font-medium text-muted-foreground mt-2' >Password must be at least 8 characters long and include at least 1 number, 1 special character, 1 uppercase and 1 lowercase letter* </p>}

            {formData.password && (
              <div className="space-y-2">
                <div className="flex justify-between text-sm">
                  <span>Password Strength:</span>
                  <span className={passwordStrength >= 75 ? 'text-green-600' : passwordStrength >= 50 ? 'text-yellow-600' : 'text-red-600'}>
                    {getPasswordStrengthLabel(passwordStrength)}
                  </span>
                </div>
                <Progress value={passwordStrength} className="h-2" />
              </div>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="confirmPassword">Confirm Password</Label>
            <div className="relative">
              <Input
                id="confirmPassword"
                type={showConfirmPassword ? 'text' : 'password'}
                value={formData.confirmPassword}
                onChange={(e) => handleInputChange('confirmPassword', e.target.value)}
                placeholder="Confirm your password"
                disabled={authState.signUpVendorResponse.isLoading || profileState.vendorProfile.isLoading || authState.isLoading}

              />
              <Button
                type="button"
                variant="none"
                size="sm"
                className="absolute right-0 top-0 h-full px-3 py-2 hover:bg-transparent hover:text-blue-800"
                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
              >
                {showConfirmPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </Button>
            </div>
            {formData.confirmPassword && (
              <div className="flex items-center gap-2 text-sm">
                {formData.password === formData.confirmPassword ? (
                  <>
                    <CheckCircle className="h-4 w-4 text-green-500" />
                    <span className="text-green-600">Passwords match</span>
                  </>
                ) : (
                  <>
                    <XCircle className="h-4 w-4 text-red-500" />
                    <span className="text-red-600">Passwords do not match</span>
                  </>
                )}
              </div>
            )}
          </div>

          <ButtonWithLoader
            loaderPosition='right'
            isLoading={authState.signUpVendorResponse.isLoading || profileState.vendorProfile.isLoading || authState.isLoading ||  authState.sendVendorSignupOtpToEmailResponse.isLoading}
            onClick={handleSubmit}
            className="w-full bg-flup-brand hover:bg-flup-brand-press text-white"
            disabled={authState.signUpVendorResponse.isLoading || authState.sendVendorSignupOtpToEmailResponse.isLoading}
          >
            {authState.signUpVendorResponse.isLoading ||  authState.sendVendorSignupOtpToEmailResponse.isLoading ? 'Creating Account...' : 'Create Account'}
          </ButtonWithLoader>

          <div className="text-center text-sm text-flup-ink-2">
            Already have an account?{' '}
            <Button variant="link" onClick={onSwitchToLogin} className="p-0 h-auto">
              Login
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
    </>
  );
};

export default SignupScreen;
